/**
 * Pot Configurator Loader
 *
 * Liquid kan per collectie maar 50 producten renderen. De pot-collectie telt er
 * 300+, dus de duurdere potten vielen structureel buiten de configurator.
 *
 * Dit script haalt na horizontale scrollintentie een al gefilterde pagina op bij
 * het JSON-endpoint templates/product.pots-json.liquid
 * (?view=pots-json&page=N). Dat endpoint gebruikt `paginate ... by 250` en
 * dezelfde matching-regel als het Liquid-blok in sections/single-product.liquid.
 * Iedere scrollbatch voegt hooguit twaalf kaarten toe.
 */

(function () {
  'use strict';

  var LOG = '[Pot Loader]';
  var BATCH_SIZE = 12;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var slider = document.querySelector('.js-oppot-slider[data-min-diameter][data-max-diameter]');
    if (!slider) return;
    var container = slider.parentNode;
    var action = container && container.querySelector('[data-pot-loader-action]');
    if (!action) return;
    var status = container.querySelector('[data-pot-loader-status]');

    var state = createState(slider);
    scheduleMeasurement(slider, state, true);
    observeSliderSize(slider, state);
    action.hidden = true;
    slider.addEventListener(
      'scroll',
      function () {
        if (state.loading || state.complete || !isNearEnd(slider, state)) return;
        loadNextBatch(slider, action, status, state);
      },
      { passive: true }
    );
    action.addEventListener('click', function (event) {
      event.preventDefault();
      if (action.hidden) return;
      loadNextBatch(slider, action, status, state);
    });
  }

  function scheduleMeasurement(slider, state, measureCard) {
    if (state.measurementFrame) cancelAnimationFrame(state.measurementFrame);
    state.measurementFrame = requestAnimationFrame(function () {
      state.measurementFrame = null;
      state.clientWidth = slider.clientWidth;
      state.scrollWidth = slider.scrollWidth;
      if (!measureCard) return;
      var card = slider.querySelector('.related-pots');
      state.cardWidth = card ? card.getBoundingClientRect().width : 0;
    });
  }

  function observeSliderSize(slider, state) {
    if (typeof ResizeObserver === 'undefined') return;
    var observer = new ResizeObserver(function () {
      scheduleMeasurement(slider, state, true);
    });
    observer.observe(slider);
  }

  function isNearEnd(slider, state) {
    if (!state.cardWidth) return false;
    var remaining = state.scrollWidth - state.clientWidth - slider.scrollLeft;
    return remaining <= state.cardWidth;
  }

  function showLoading(slider) {
    var container = slider.parentNode;
    if (!container || container.querySelector('.pot-loading-skeleton')) return;

    slider.setAttribute('aria-busy', 'true');
    var skeleton = document.createElement('div');
    skeleton.className = 'pot-loading-skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    skeleton.innerHTML = '<span class="pot-loading-skeleton__card"></span>'.repeat(4);
    container.appendChild(skeleton);
  }

  function hideLoading(slider) {
    slider.removeAttribute('aria-busy');
    var container = slider.parentNode;
    var skeleton = container && container.querySelector('.pot-loading-skeleton');
    if (skeleton) skeleton.remove();
  }

  function createState(slider) {
    var state = {
      groups: [],
      loading: false,
      complete: false,
      cardWidth: 0,
      clientWidth: 0,
      scrollWidth: 0,
      measurementFrame: null,
      nextPage: 1,
      pages: null,
      renderedProducts: {},
      renderedVariants: {},
    };

    Array.prototype.forEach.call(slider.querySelectorAll('.related-pots'), function (card) {
      var input = card.querySelector('input');
      if (input) {
        state.renderedVariants[input.value] = true;
        state.renderedProducts[input.dataset.productId] = true;
      }
      (card.dataset.familyIds || '').split(',').forEach(function (id) {
        if (id) state.renderedProducts[id] = true;
      });
    });

    return state;
  }

  async function loadNextBatch(slider, action, status, state) {
    if (state.loading || state.complete) return;

    state.loading = true;
    action.hidden = true;
    action.setAttribute('aria-disabled', 'true');
    setStatus(status, action.dataset.loadingLabel);
    showLoading(slider);

    try {
      var batch = [];
      while (batch.length < BATCH_SIZE) {
        if (state.groups.length) {
          batch.push(state.groups.shift());
          continue;
        }

        if (state.pages !== null && state.nextPage > state.pages) break;

        var result = await fetchPage(state.nextPage);
        state.nextPage += 1;
        state.pages = Math.max(0, Number(result.pages || 0));
        queuePage(state, result.items);

        if ((result.collections_total || 1) > 1) {
          console.warn(LOG, 'Plant verwijst naar meer dan een pot-collectie; alleen de eerste wordt volledig geladen.');
        }
      }

      render(slider, batch, state);
      state.complete = state.groups.length === 0 && state.pages !== null && state.nextPage > state.pages;
      action.hidden = true;
      action.textContent = action.dataset.retryLabel || action.textContent;
      setStatus(
        status,
        countLabel(state.complete ? action.dataset.completeLabel : action.dataset.addedLabel, batch.length)
      );
    } catch (error) {
      state.groups = batch.concat(state.groups);
      console.error(LOG, 'Laden mislukt:', error);
      action.hidden = false;
      action.textContent = action.dataset.retryLabel || action.dataset.actionLabel || action.textContent;
      setStatus(status, action.dataset.errorLabel);
    } finally {
      state.loading = false;
      action.removeAttribute('aria-disabled');
      hideLoading(slider);
    }
  }

  function setStatus(status, message) {
    if (status) status.textContent = message || '';
  }

  function countLabel(template, count) {
    return String(template || '').replace('__COUNT__', String(count));
  }

  function fetchPage(page) {
    var url = window.location.pathname + '?view=pots-json&page=' + page;

    // Geen Accept: application/json meesturen. Shopify geeft dan de standaard
    // product-JSON terug in plaats van de alternatieve template.
    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var data = JSON.parse(text);
        if (!data || !Array.isArray(data.items)) {
          throw new Error('Onverwacht antwoord van ?view=pots-json');
        }
        return data;
      });
  }

  function queuePage(state, items) {
    var fresh = items.filter(function (item) {
      if (state.renderedVariants[item.variant_id] || state.renderedProducts[item.product_id]) return false;
      state.renderedVariants[item.variant_id] = true;
      state.renderedProducts[item.product_id] = true;
      return true;
    });

    if (!fresh.length) return;

    var groups = groupByFamily(fresh);
    groups.forEach(function (group) {
      group.sort(function (a, b) {
        return Number(a.price) - Number(b.price);
      });
    });
    groups.sort(function (a, b) {
      return Number(a[0].price) - Number(b[0].price);
    });
    state.groups = state.groups.concat(groups);
  }

  function render(slider, groups, state) {
    if (!groups.length) return;

    var labels = {
      familySummary: slider.dataset.familySummaryTemplate || '__COUNT__',
    };

    groups.forEach(function (group) {
      // Kleurwissel behoudt de maat: alleen kleuren met exact dezelfde
      // variant-titel (D x H) als de anker-kaart krijgen een bolletje.
      var anchor = group[0];
      var sameSize = group.filter(function (item) {
        return item.variant_title === anchor.variant_title;
      });
      slider.appendChild(createPotElement(anchor, sameSize.length > 1 ? sameSize : null, labels));
    });

    sortSlides(slider);
    scheduleMeasurement(slider, state, false);

    // De voortgangsbalk berekent zijn breedte kort na DOMContentLoaded, dus voor
    // deze fetch klaar is. Een scroll-event laat hem opnieuw rekenen.
    slider.dispatchEvent(new Event('scroll'));
  }

  /**
   * Groepeer items van dezelfde potkleur-familie tot een kaart. Items zonder
   * kleurnaam, zonder hex of zonder familie blijven een eigen kaart - fail-open,
   * exact zoals voor de kleurbolletjes.
   */
  function groupByFamily(items) {
    var groups = [];
    var byKey = {};

    items.forEach(function (item) {
      var key = familyKey(item);
      if (!key) {
        groups.push([item]);
        return;
      }
      if (!byKey[key]) {
        byKey[key] = [];
        groups.push(byKey[key]);
      }
      byKey[key].push(item);
    });

    return groups;
  }

  function familyKey(item) {
    if (!item.color_name || !item.color_hex) return null;
    var ids = (item.family_ids || []).map(Number).filter(Boolean);
    if (ids.length < 2) return null;
    // Elk familielid draagt dezelfde lijst, dus het laagste id is een stabiele sleutel.
    return String(Math.min.apply(null, ids));
  }

  function createPotElement(item, family, labels) {
    var priceParts = String(item.price_formatted || '').split(',');
    var potDiameter = item.pot_diameter || 0;
    var hasFamily = Boolean(family && window.PotColorSwatches);
    var potFamilyId = hasFamily
      ? String(
          Math.min.apply(
            null,
            family.map(function (member) {
              return Number(member.product_id);
            })
          )
        )
      : '';

    var div = document.createElement('div');
    div.className = 'related-pots rounded-2 position-relative js-lazy-loaded' + (hasFamily ? ' related-pots--family' : '');
    if (hasFamily) {
      div.dataset.potFamilyId = potFamilyId;
      div.dataset.familyIds = family
        .map(function (member) {
          return member.product_id;
        })
        .join(',');
    }
    // data-order 0: het JSON-endpoint pagineert de vaste potcollectie van de plant.
    div.dataset.order = 0;
    div.dataset.suborder = item.price;

    div.innerHTML =
      '<input class="related-pots__input" type="radio" name="addExtraPot"' +
      ' id="pot-' + item.variant_id + '"' +
      ' data-diameter="' + (potDiameter || '') + '"' +
      ' data-product-id="' + item.product_id + '"' +
      ' data-pot-price="' + item.price + '"' +
      ' data-pot-compare-price="' + (item.compare_at_price || 0) + '"' +
      ' value="' + item.variant_id + '">' +
      '<label class="js-select-pot related-pots__label" for="pot-' + item.variant_id + '">' +
      '<span class="related-pots__price d-flex align-items-start">' +
      '<span class="lh-1 fw-medium">' + escapeHtml(priceParts[0]) + ',</span>' +
      '<span class="lh-1 fw-medium">' + escapeHtml(priceParts[1] || '') + '</span>' +
      '</span>' +
      '<img class="img-abs-center" src="' + encodeURI(item.image_200 || '') + '"' +
      ' alt="' + escapeHtml(item.image_alt || item.title) + '"' +
      ' width="100" height="100" loading="lazy" decoding="async">' +
      (potDiameter
        ? '<span class="related-pots__diameter">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="5" y1="11" x2="5" y2="13"/><line x1="19" y1="11" x2="19" y2="13"/></svg>' +
          potDiameter + 'cm</span>'
        : '') +
      '</label>' +
      '<div class="related-pots__view js-quickview-toggle"' +
      ' data-title="' + escapeHtml(item.title + ' - ' + item.variant_title) + '"' +
      ' data-variant-title="' + escapeHtml(item.variant_title) + '"' +
      ' data-price="' + item.price + '"' +
      ' data-description="' + escapeHtml(item.description || '') + '"' +
      ' data-url="' + encodeURI(item.url) + '?variant=' + item.variant_id + '"' +
      ' data-image="' + encodeURI(item.image_600 || '') + '"' +
      ' data-bs-toggle="modal" data-bs-target="#exampleModal">' +
      '<i class="fa-sharp fa-solid fa-magnifying-glass"></i>' +
      '</div>' +
      (hasFamily
        ? window.PotColorSwatches.buildFamilyCardHtml(family, labels, potFamilyId)
        : '');

    return div;
  }

  /**
   * Zelfde sortering als initOppotConfigurator in app.js: op data-order, daarna
   * op prijs. Die functie draait al voordat deze fetch klaar is, dus de nieuwe
   * slides moeten hier zelf op hun plek gezet worden.
   */
  function sortSlides(slider) {
    var slides = Array.prototype.slice.call(slider.children);

    slides.sort(function (a, b) {
      var orderDiff = Number(a.dataset.order || 0) - Number(b.dataset.order || 0);
      if (orderDiff !== 0) return orderDiff;
      return Number(a.dataset.suborder || 0) - Number(b.dataset.suborder || 0);
    });

    slides.forEach(function (slide) {
      slider.appendChild(slide);
    });
  }

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text == null ? '' : text;
    return div.innerHTML.replace(/"/g, '&quot;');
  }
})();
